import { mdiAccount, mdiPail, mdiPailOutline } from '@mdi/js'
import Icon from '@mdi/react'
import Paper from '@mui/material/Paper'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router'
import { Bucket } from 'shared/types'
import { getGradientStyle, lightenColor } from 'utils/colors'
import styles from './BucketCard.pcss'
import { PostDestination } from 'shared'

export type BucketWithProfiles = Bucket & { profiles: PostDestination[] }

export interface BucketCardProps {
  bucket: BucketWithProfiles
  navigation?: string
  style?: React.CSSProperties
  className?: string
  onClick?: (bucket: BucketWithProfiles) => void
}

const MAX_IMAGES = 3
const LIGHTEN_AMOUNT = 0.25
const LOW_POSTS_THRESHOLD = 5

function getPostsBadgeTier(count: number): 'posts-badge-empty' | 'posts-badge-low' | 'posts-badge-high' {
  if (count === 0) return 'posts-badge-empty'
  if (count <= LOW_POSTS_THRESHOLD) return 'posts-badge-low'
  return 'posts-badge-high'
}

interface AvatarImageProps {
  url?: string
  className: string
  children?: React.ReactNode
}

function AvatarImage({ url, className, children }: AvatarImageProps) {
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    setFailed(false)
    if (!url) {
      return
    }
    const img = new Image()
    img.onerror = () => setFailed(true)
    img.src = url
  }, [url])

  const showFallback = !url || failed
  const style = showFallback ? undefined : { backgroundImage: `url(${url})` }
  return (
    <div className={`${className} ${showFallback ? styles['img-box-fallback'] : ''}`} style={style}>
      {showFallback && <Icon path={mdiAccount} color="#fff" size="80%" />}
      {children}
    </div>
  )
}

export function BucketCard(props: BucketCardProps) {
  const { bucket, navigation, style, className, onClick } = props
  const elementRef = React.useRef<HTMLDivElement>(null)

  const onMouseEnter = () => {
    if (elementRef.current) {
      elementRef.current.classList.add(styles.raised)
    }
  }

  const handleClick = () => {
    if (onClick) {
      onClick(bucket)
    }
  }

  const colorStyle = getGradientStyle(`${bucket.color},${lightenColor(bucket.color, LIGHTEN_AMOUNT)}`, 'bottom right')
  const profilesCount = bucket.profiles.length
  const showAllThree = profilesCount >= MAX_IMAGES
  const images = bucket.profiles.slice(0, MAX_IMAGES).map(p => p.image)

  const featuredImage = images[0]
  const secondaryImage1 = images[1]
  const secondaryImage2 = images[2]

  const content = (
    <React.Fragment>
      <h5 className={styles.title} title={`Bucket: ${bucket.name}`} data-testid="bucket-name">
        <Icon path={mdiPail} size="14px" className={styles.icon} />
        <span className="text-ellipsis">{bucket.name}</span>
      </h5>
      <div className={styles.images}>
        {showAllThree && <AvatarImage url={secondaryImage1} className={styles['img-box']} />}
        <AvatarImage url={featuredImage} className={`${styles['img-box']} ${styles.featured}`}>
          <div className={styles.overlay}>
            <p className={styles['stats-large']}>{profilesCount}</p>
            <p>socials</p>
          </div>
        </AvatarImage>
        {showAllThree && <AvatarImage url={secondaryImage2} className={styles['img-box']} />}
      </div>
    </React.Fragment>
  )

  return (
    <div
      ref={elementRef}
      className={`${styles.container} ${className || ''}`}
      data-testid="bucket-card"
      onMouseEnter={onMouseEnter}
      style={style}
    >
      <Paper
        className={`${styles.paper} ${styles.card}`}
        style={colorStyle}
        onClick={handleClick}
      >
        <div className={styles.watermark}>
          <Icon path={mdiPailOutline} size="100px" />
        </div>
        {navigation ? (
          <NavLink to={navigation}>{content}</NavLink>
        ) : (
          content
        )}
      </Paper>
      <span className={`${styles['posts-badge']} ${styles[getPostsBadgeTier(bucket.postsCount)]}`}>
        <FormattedMessage id="post.labels.posts-count" values={{ count: bucket.postsCount }} />
      </span>
    </div>
  )
}

export default BucketCard
